import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { EnhancedHeader } from '@/components/common/EnhancedHeader';
import { 
  Bell, 
  Package, 
  MessageSquare, 
  Calendar, 
  AlertCircle, 
  CheckCircle,
  Search
} from 'lucide-react';
import { apiUrl } from '@/config/api';

interface CustomerNotification {
  id: string;
  type: 'order' | 'message' | 'appointment' | 'system' | 'payment';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
  priority: 'low' | 'medium' | 'high';
}

const CustomerNotifications: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<CustomerNotification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<CustomerNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'unread' | 'order' | 'message' | 'appointment' | 'system' | 'payment'>('all');

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    filterNotifications();
  }, [notifications, searchQuery, filterType]);

  const loadNotifications = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        // No token - show empty notifications
        setNotifications([]);
        return;
      }

      const response = await fetch(apiUrl('/api/notifications/'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const customerNotifications: CustomerNotification[] = (data.notifications || []).map((notif: any) => ({
          id: notif.id.toString(),
          type: notif.type || 'system',
          title: notif.title || 'Notification',
          message: notif.message || notif.content || 'You have a new notification',
          timestamp: notif.created_at || notif.timestamp || new Date().toISOString(),
          read: notif.read || false,
          actionUrl: getActionUrl(notif.type, notif.data),
          priority: notif.priority || 'medium'
        }));
        
        setNotifications(customerNotifications);
      } else {
        console.log('Customer notifications API failed, showing empty notifications');
        setNotifications([]);
      }
    } catch (_error) {
      console.log('Customer notifications fetch failed, showing empty notifications:', error);
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  };


  const getActionUrl = (type: string, data: any): string => {
    switch (type) {
      case 'order':
        // Navigate directly to the order timeline for consistent UX
        return data?.order_id ? `/order/${data.order_id}/details` : '/customer/orders';
      case 'message':
        return '/customer/chat';
      case 'appointment':
        return '/customer/dashboard?tab=appointments';
      case 'payment':
        return '/customer/orders';
      case 'system':
        return '/customer/dashboard';
      default:
        return '/customer/dashboard';
    }
  };

  const filterNotifications = () => {
    let filtered = notifications;

    // Filter by type
    if (filterType !== 'all') {
      if (filterType === 'unread') {
        filtered = filtered.filter(n => !n.read);
      } else {
        filtered = filtered.filter(n => n.type === filterType);
      }
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(n => 
        n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.message.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredNotifications(filtered);
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetch(apiUrl(`/api/notifications/${notificationId}/read`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (_error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetch(apiUrl('/api/customer/notifications/mark-all-read'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );
    } catch (_error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
      case 'message':
        return <MessageSquare className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'appointment':
        return <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />;
      case 'payment':
        return <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'system':
        return <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500 bg-red-50 dark:bg-red-900/20';
      case 'medium':
        return 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
      case 'low':
        return 'border-l-green-500 bg-green-50 dark:bg-green-900/20';
      default:
        return 'border-l-gray-500 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    // Simple timestamp formatting - in real app, use a proper date library
    return timestamp;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <EnhancedHeader title="Notifications" showBackButton backTo="/customer/dashboard" />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <EnhancedHeader title="Notifications" showBackButton backTo="/customer/dashboard" />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Notifications</h1>
              <p className="text-gray-600 dark:text-slate-400">
                {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Mark All Read</span>
              </button>
            )}
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-100"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-slate-100"
            >
              <option value="all">All Notifications</option>
              <option value="unread">Unread Only</option>
              <option value="order">Orders</option>
              <option value="message">Messages</option>
              <option value="appointment">Appointments</option>
              <option value="payment">Payments</option>
              <option value="system">System</option>
            </select>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg overflow-hidden">
          {filteredNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">
                {searchQuery || filterType !== 'all' ? 'No matching notifications' : 'No notifications yet'}
              </h3>
              <p className="text-gray-600 dark:text-slate-400">
                {searchQuery || filterType !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'You\'ll receive notifications about your orders, appointments, and messages here.'
                }
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-slate-700">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-6 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors border-l-4 ${getPriorityColor(notification.priority)} ${
                    !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className={`text-lg font-medium ${!notification.read ? 'text-gray-900 dark:text-slate-100' : 'text-gray-700 dark:text-slate-300'}`}>
                          {notification.title}
                        </h3>
                        <div className="flex items-center space-x-2">
                          {!notification.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                          <span className="text-sm text-gray-500 dark:text-slate-400">
                            {formatTimestamp(notification.timestamp)}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-slate-400 mt-1">
                        {notification.message}
                      </p>
                      <div className="flex items-center space-x-4 mt-3">
                        {!notification.read && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                          >
                            Mark as read
                          </button>
                        )}
                        {notification.actionUrl && (
                          <button
                            onClick={() => {
                              markAsRead(notification.id);
                              window.location.href = notification.actionUrl;
                            }}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                          >
                            View details
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerNotifications;
